import { useEffect, useState, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Loader2, Search, RefreshCw, TrendingUp, Users, Key } from "lucide-react";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { TableSortHeader, useTableSort } from "./TableSortHeader";
import { StatusFilter } from "./StatusFilter";

interface Token {
  id: string;
  code: string;
  status: string;
  purchase_email: string;
  purchase_id: string;
  stripe_session_id: string | null;
  created_at: string;
}

interface Purchase {
  id: string;
  email: string;
  tokenCount: number;
  usedCount: number;
  createdAt: string;
  stripeSessionId: string | null;
}

const PRODUCT_OPTIONS = [
  { value: "all", label: "All products" },
  { value: "1", label: "Individual" },
  { value: "6", label: "Bundle of 6" },
  { value: "10", label: "Bundle of 10" },
];

export const AdminPurchasesTab = () => {
  const [tokens, setTokens] = useState<Token[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [productFilter, setProductFilter] = useState("all");
  const { toast } = useToast();
  const { sortKey, sortDirection, handleSort, sortData } = useTableSort<Purchase>("createdAt", "desc");

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('access_tokens')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTokens(data || []);
    } catch (error) {
      toast({
        title: "Error loading data",
        description: "Failed to load purchase data. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const productionTokens = useMemo(() => 
    tokens.filter(token => !token.code.startsWith('TEST-')), 
    [tokens]
  );

  const purchases = useMemo(() => {
    const purchaseMap = new Map<string, Purchase>();

    productionTokens.forEach(token => {
      const existing = purchaseMap.get(token.purchase_id);
      if (existing) {
        existing.tokenCount++;
        if (token.status === 'used') existing.usedCount++;
      } else {
        purchaseMap.set(token.purchase_id, {
          id: token.purchase_id,
          email: token.purchase_email,
          tokenCount: 1,
          usedCount: token.status === 'used' ? 1 : 0,
          createdAt: token.created_at,
          stripeSessionId: token.stripe_session_id,
        });
      }
    });

    return Array.from(purchaseMap.values());
  }, [productionTokens]);

  const filteredPurchases = useMemo(() => {
    let result = purchases;

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(purchase =>
        purchase.email.toLowerCase().includes(searchLower) ||
        purchase.id.toLowerCase().includes(searchLower)
      );
    }

    if (productFilter !== "all") {
      const filterCount = parseInt(productFilter, 10);
      result = result.filter(purchase => purchase.tokenCount === filterCount);
    }

    return result;
  }, [purchases, searchTerm, productFilter]);

  const sortedPurchases = useMemo(() => sortData(filteredPurchases), [filteredPurchases, sortKey, sortDirection]);

  const totalRevenue = purchases.reduce((sum, p) => {
    if (p.tokenCount === 1) return sum + 119;
    if (p.tokenCount === 6) return sum + 600;
    if (p.tokenCount === 10) return sum + 1000;
    return sum + (p.tokenCount * 100);
  }, 0);

  const totalTokens = productionTokens.length;
  const totalPurchases = purchases.length;

  const getProductName = (tokenCount: number) => {
    switch (tokenCount) {
      case 1: return "Individual";
      case 6: return "Bundle of 6";
      case 10: return "Bundle of 10";
      default: return `${tokenCount} tokens`;
    }
  };

  const getEstimatedPrice = (tokenCount: number) => {
    switch (tokenCount) {
      case 1: return "£119";
      case 6: return "£600";
      case 10: return "£1,000";
      default: return `~£${tokenCount * 100}`;
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-primary/10 rounded-full">
                <TrendingUp className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Est. Revenue</p>
                <p className="text-2xl font-bold">£{totalRevenue.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-primary/10 rounded-full">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Purchases</p>
                <p className="text-2xl font-bold">{totalPurchases}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-primary/10 rounded-full">
                <Key className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Tokens</p>
                <p className="text-2xl font-bold">{totalTokens}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle>Purchase History</CardTitle>
              <CardDescription>
                All completed purchases and token bundles
              </CardDescription>
            </div>
            <div className="flex flex-wrap gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search by email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 w-48"
                />
              </div>
              <StatusFilter
                value={productFilter}
                onValueChange={setProductFilter}
                options={PRODUCT_OPTIONS}
                placeholder="Filter by product"
              />
              <Button variant="outline" size="icon" onClick={fetchData}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : sortedPurchases.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              {searchTerm || productFilter !== "all" ? "No results match your filters" : "No purchases found"}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      <TableSortHeader
                        label="Email"
                        sortKey="email"
                        currentSortKey={sortKey}
                        currentDirection={sortDirection}
                        onSort={handleSort}
                      />
                    </TableHead>
                    <TableHead>
                      <TableSortHeader
                        label="Product"
                        sortKey="tokenCount"
                        currentSortKey={sortKey}
                        currentDirection={sortDirection}
                        onSort={handleSort}
                      />
                    </TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>
                      <TableSortHeader
                        label="Tokens Used"
                        sortKey="usedCount"
                        currentSortKey={sortKey}
                        currentDirection={sortDirection}
                        onSort={handleSort}
                      />
                    </TableHead>
                    <TableHead>
                      <TableSortHeader
                        label="Date"
                        sortKey="createdAt"
                        currentSortKey={sortKey}
                        currentDirection={sortDirection}
                        onSort={handleSort}
                      />
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedPurchases.map((purchase) => (
                    <TableRow key={purchase.id}>
                      <TableCell className="font-medium">{purchase.email}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">
                          {getProductName(purchase.tokenCount)}
                        </Badge>
                      </TableCell>
                      <TableCell>{getEstimatedPrice(purchase.tokenCount)}</TableCell>
                      <TableCell>
                        {purchase.usedCount} / {purchase.tokenCount}
                      </TableCell>
                      <TableCell>
                        {format(new Date(purchase.createdAt), "dd MMM yyyy HH:mm")}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
